import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Modal, notification } from "antd";
import classNames from "classnames";
import { get, isEmpty, isEqual, isNil, keyBy, uniq } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdDeleteOutline,
  MdDownload,
  MdDriveFolderUpload,
  MdLink,
  MdUploadFile,
} from "react-icons/md";
import {
  addEdge,
  Background,
  Panel,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import cuid from "cuid";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import AddStepDialog from "./AddStepDialog";
import CanvasIcon from "./CanvasIcon";
import {
  CanvasSelectionContext,
  CHIP_KINDS,
  withChip,
  withMovedStates,
  withOnlyBlock,
  withoutChip,
} from "./canvas-selection";
import { withRequesterVariables } from "./catalog";
import ChecksPanel from "./ChecksPanel";
import ClaudePanel from "./ClaudePanel";
import SideRail, { SIDE_PANELS } from "./SideRail";
import StepSidePanel from "./StepSidePanel";
import { DRAFT_ERRORS, draftErrorOf } from "./draft-errors";
import { decorateEdges } from "./edge-look";
import GuestLinkDialog from "./GuestLinkDialog";
import { applyPlanToGraph } from "./folder-import";
import FolderImportModal from "./FolderImportModal";
import ImportSummaryModal from "./ImportSummaryModal";
import JourneyOverlay from "./JourneyOverlay";
import { simulateJourney, simulationFields } from "./journey-simulation";
import RuleEdge from "./RuleEdge";
import SimulationPanel from "./SimulationPanel";
import StatusNode from "./StatusNode";
import StatusPanel, { SIDE_TABS } from "./StatusPanel";
import { checkedFieldsOf } from "./checked-fields";
import { needsPathsOf } from "./needs-fields";
import {
  needsFieldsOf,
  needsUsage,
  RecommendationContext,
  recommendationChecklist,
} from "./recommendation-guide";
import { checksByStep, checkTab, endSteps, worstLevel } from "./step-checks";
import { ICON_PATHS } from "./step-look";
import { STEP_TONES, stepTone } from "./step-summary";
import { STEP_TABS } from "./step-tabs";
import StepList, { orderSteps, startFrom } from "./StepList";
import { isBackEdge } from "./next-steps";
import {
  applyLayout,
  LAYOUT_DIRECTIONS,
  layoutDirection,
  newStepPosition,
  withHandles,
} from "./layout";
import {
  draftScreenCount,
  draftScreenStepIds,
  draftStatuses,
  fromGraph,
  newRuleEdge,
  newStatusNode,
  stepScreens,
  toGraph,
  withScreenData,
  withSharedScreenNames,
} from "./graph";
import {
  attachGraphScreens,
  processToConfig,
  serializeConfig,
} from "./importConfig";
import useDraftAutosave, { SAVE_OUTCOMES } from "./use-draft-autosave";
import { WORKSHOP_ICONS } from "./workshop-view";

const nodeTypes = { status: StatusNode };

const edgeTypes = { rule: RuleEdge };

const fitViewOptions = {
  padding: { top: "96px", bottom: "96px", x: "64px" },
  maxZoom: 1,
};

const FOCUS_MIN_ZOOM = 0.75;
const ZOOM_DURATION = 200;

const MORE_ACTIONS = {
  VERIFY: "verify",
  SIMULATE: "simulate",
  GUEST_LINK: "guestLink",
  IMPORT: "import",
  IMPORT_FOLDER: "importFolder",
  EXPORT: "export",
  DISCARD: "discard",
};

const DRAFT_TONES = {
  ONLINE: "online",
  DRAFT: "draft",
  SAVING: "saving",
  ERROR: "error",
};

const FOLDER_PICKER = { webkitdirectory: "", directory: "" };

const LEGEND_TONES = [STEP_TONES.OK, STEP_TONES.WARNING, STEP_TONES.ERROR];

const clone = (value) => JSON.parse(JSON.stringify(value));

const changedStepIds = (before = [], after = []) => {
  const beforeById = keyBy(before, "id");
  const afterById = keyBy(after, "id");
  return uniq([...Object.keys(beforeById), ...Object.keys(afterById)]).filter(
    (id) => !isEqual(beforeById[id], afterById[id]),
  );
};

const placeGraph = (graph) => {
  const hasSavedPositions = graph.nodes.some(
    (node) => node.position && (node.position.x || node.position.y),
  );
  const direction = hasSavedPositions
    ? layoutDirection(graph.nodes)
    : LAYOUT_DIRECTIONS.TOP_BOTTOM;
  return {
    direction,
    nodes: hasSavedPositions
      ? withHandles(graph.nodes, direction)
      : applyLayout(graph.nodes, graph.edges, direction),
  };
};

const SIDE_PANEL_KEY = "canvas.side.panel";

const CLOSED_SIDE_PANEL = "none";

const readSidePanel = () => {
  try {
    const stored = localStorage.getItem(SIDE_PANEL_KEY);
    if (stored === CLOSED_SIDE_PANEL) return null;
    return Object.values(SIDE_PANELS).includes(stored)
      ? stored
      : SIDE_PANELS.LUCIE;
  } catch (_) {
    return SIDE_PANELS.LUCIE;
  }
};

const View = ({
  process = {},
  draft = null,
  variables = [],
  cancel,
  reload,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const FORM = t("FORM", { returnObjects: true });
  const EDITOR = t("CANVAS_EDITOR", { returnObjects: true });
  const DRAFT = EDITOR.DRAFT;
  const CANVASES = t("CANVASES", { returnObjects: true });
  const JOURNEY = EDITOR.JOURNEY;
  const PROPOSAL = EDITOR.CLAUDE.PROPOSAL;
  const LEAVE = EDITOR.WORKSPACE.LEAVE;
  const THEMES = t("TEMPLATES_THEMES", { returnObjects: true }) || {};
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [importBase, setImportBase] = useState(null);
  const [folderFiles, setFolderFiles] = useState(null);
  const [flow, setFlow] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [chips, setChips] = useState([]);
  const [journeyOpen, setJourneyOpen] = useState(false);
  const [focusedEdgeId, setFocusedEdgeId] = useState(null);
  const [panelTab, setMiddleTab] = useState(STEP_TABS.MAIN);
  const [sideTab, setSideTab] = useState(STEP_TABS.NEXT);
  const [sidePanel, setSidePanel] = useState(readSidePanel);
  const [sideBody, setSideBody] = useState(null);
  const [sideMeta, setSideMeta] = useState(null);
  useEffect(() => {
    try {
      localStorage.setItem(SIDE_PANEL_KEY, sidePanel || CLOSED_SIDE_PANEL);
    } catch (_) {}
  }, [sidePanel]);
  const setPanelTab = useCallback((key) => {
    if (
      SIDE_TABS.includes(key) ||
      [STEP_TABS.AUTOMATIONS, STEP_TABS.FIELDS].includes(key)
    ) {
      setSideTab(key);
      setSidePanel(SIDE_PANELS.SETTINGS);
      return;
    }
    setMiddleTab(key);
  }, []);
  const [showBack, setShowBack] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [checksOpen, setChecksOpen] = useState(false);
  const [recommendationFocus, setRecommendationFocus] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [direction, setDirection] = useState(LAYOUT_DIRECTIONS.TOP_BOTTOM);
  const [liveSnapshot, setLiveSnapshot] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [zoom, setZoom] = useState(1);
  const baseStatuses = useRef([]);
  const loadedProcessId = useRef(null);
  const leaveGuard = useRef(null);
  const closeGuest = useCallback(() => setGuestOpen(false), []);
  const [discardProcessDraft] = useMutation(
    graphql.mutations.DISCARD_PROCESS_DRAFT,
    { onError: () => undefined },
  );
  const [publishCanvas] = useMutation(graphql.mutations.PUBLISH_CANVAS, {
    onError: () => undefined,
  });
  const [applyCanvasProposal] = useMutation(
    graphql.mutations.APPLY_CANVAS_PROPOSAL,
    { refetchQueries: ["FieldRegistry"], onError: () => undefined },
  );

  const snapshot = useCallback(
    (graphNodes, graphEdges) =>
      clone(fromGraph(graphNodes, graphEdges, baseStatuses.current)),
    [],
  );

  const autosave = useDraftAutosave({
    process,
    nodes,
    edges,
    snapshot,
    onConflict: () => handleConflict(),
  });
  const { reset, current: currentStatuses } = autosave;

  const rebuild = useCallback(
    ({ process: live, draft: stored }) => {
      const statuses = draftStatuses({ process: live, draft: stored });
      const graph = toGraph(statuses);
      const placed = placeGraph(graph);
      baseStatuses.current = statuses;
      const draftSnapshot = clone(
        fromGraph(placed.nodes, graph.edges, statuses),
      );
      const liveStatuses = get(live, "processStatuses") || [];
      const liveGraph = toGraph(liveStatuses);
      setLiveSnapshot(
        isNil(stored)
          ? draftSnapshot
          : clone(
              fromGraph(
                placeGraph(liveGraph).nodes,
                liveGraph.edges,
                liveStatuses,
              ),
            ),
      );
      setDirection(placed.direction);
      setNodes(placed.nodes);
      setEdges(graph.edges);
      reset({
        statuses: draftSnapshot,
        revision: get(stored, "revision") || 0,
      });
      setFocusedEdgeId(null);
      setSelectedNodeId((current) =>
        graph.nodes.some((node) => node.id === current) ? current : null,
      );
    },
    [reset, setNodes, setEdges],
  );

  useEffect(() => {
    if (loadedProcessId.current === process.id) return;
    loadedProcessId.current = process.id;
    rebuild({ process, draft });
  }, [process, draft, rebuild]);

  const screensByStep = useMemo(
    () => stepScreens({ process, draft }),
    [process, draft],
  );

  useEffect(() => {
    if (loadedProcessId.current !== process.id) return;
    setNodes((current) => withScreenData(current, screensByStep));
  }, [screensByStep, setNodes, process.id]);

  const liveStepIds = useMemo(
    () => (process.processStatuses || []).map((status) => status.id),
    [process],
  );
  const draftScreens = useMemo(
    () => draftScreenCount({ process, draft }),
    [process, draft],
  );
  const changedIds = useMemo(
    () =>
      isNil(currentStatuses)
        ? []
        : changedStepIds(liveSnapshot, currentStatuses.statuses),
    [liveSnapshot, currentStatuses],
  );
  const screenDraftStepIds = useMemo(
    () => draftScreenStepIds({ process, draft }),
    [process, draft],
  );
  const changeCount = uniq([...changedIds, ...screenDraftStepIds]).length;
  const hasDraft = autosave.revision > 0 || draftScreens > 0;
  const unsaved = autosave.pending || autosave.saving || autosave.failing;
  const busy = publishing || applying;
  const publishable = (hasDraft || unsaved) && !busy;

  const refreshFromServer = async () => {
    const fresh = await Promise.resolve(reload ? reload() : null).catch(
      () => null,
    );
    if (isNil(get(fresh, "process"))) return false;
    rebuild(fresh);
    return true;
  };

  const refreshScreens = () =>
    Promise.resolve(reload ? reload() : null).catch(() => null);

  const warnConflict = () =>
    notification.warning({ message: DRAFT.ERRORS.DRAFT_CONFLICT });

  const handleConflict = () =>
    autosave.hold(async () => {
      warnConflict();
      return refreshFromServer();
    });

  const editorStatuses = () =>
    snapshot(nodes, edges).map((status) => ({
      ...status,
      screens: screensByStep[status.id] || [],
    }));

  const onImport = ({ target }) => {
    const file = target.files && target.files[0];
    if (file) {
      setImportBase({ ...process, processStatuses: editorStatuses() });
      setPendingFile(file);
    }
    target.value = "";
  };

  const openImport = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const closeImport = () => {
    setPendingFile(null);
    refreshScreens();
  };

  const putImportedSteps = (statuses) => {
    const graph = toGraph(statuses);
    const placed = placeGraph(graph);
    setDirection(placed.direction);
    setNodes(withScreenData(placed.nodes, screensByStep));
    setEdges(graph.edges);
    setFocusedEdgeId(null);
    setSelectedNodeId(null);
  };

  const onPickFolder = ({ target }) => {
    const picked = Array.from(target.files || []);
    if (picked.length) setFolderFiles(picked);
    target.value = "";
  };

  const openFolderImport = () => {
    if (folderInputRef.current) folderInputRef.current.click();
  };

  const closeFolderImport = ({ changed }) => {
    setFolderFiles(null);
    if (changed) refreshScreens();
  };

  useEffect(() => {
    if (!unsaved) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [unsaved]);

  const leave = () => {
    if (!unsaved) return cancel();
    return Modal.confirm({
      title: EDITOR.LEAVE_TITLE,
      content: EDITOR.LEAVE_CONTENT,
      okText: EDITOR.LEAVE_OK,
      cancelText: EDITOR.LEAVE_CANCEL,
      onOk: cancel,
    });
  };

  const focusNode = (node) => {
    if (!flow || !node) return;
    const width = get(node, "measured.width") || 240;
    const height = get(node, "measured.height") || 120;
    flow.setCenter(node.position.x + width / 2, node.position.y + height / 2, {
      zoom: Math.max(flow.getZoom(), FOCUS_MIN_ZOOM),
      duration: 300,
    });
  };

  const pickStep = (id) => {
    setFocusedEdgeId(null);
    setSelectedNodeId(id);
    setSelectedStateId(null);
    setNodes((current) =>
      current.some((node) => Boolean(node.selected) !== (node.id === id))
        ? current.map((node) => ({ ...node, selected: node.id === id }))
        : current,
    );
  };

  const cite = useCallback(
    (chip) => setChips((current) => withChip(current, chip)),
    [],
  );

  const citeOnly = useCallback(
    (chip) => setChips((current) => withOnlyBlock(current, chip)),
    [],
  );

  const uncite = useCallback(
    (chip) => setChips((current) => withoutChip(current, chip)),
    [],
  );

  const guardLeave = useCallback((check) => {
    leaveGuard.current = check;
    return () => {
      if (leaveGuard.current === check) leaveGuard.current = null;
    };
  }, []);

  const moveStates = useCallback((stepId, moves) => {
    setChips((current) => withMovedStates(current, { stepId, moves }));
    setSelectedStateId((current) => moves[current] || current);
  }, []);

  const confirmLeave = (target, proceed) => {
    const reason = isNil(leaveGuard.current)
      ? null
      : leaveGuard.current(target);
    if (isNil(reason)) {
      proceed();
      return;
    }
    Modal.confirm({
      title: LEAVE.TITLE,
      content: LEAVE[reason],
      okText: LEAVE.OK,
      cancelText: LEAVE.CANCEL,
      okButtonProps: { danger: true },
      onOk: proceed,
    });
  };
  const confirmLeaveRef = useRef(confirmLeave);
  confirmLeaveRef.current = confirmLeave;

  const citeStep = (id) =>
    cite({ kind: CHIP_KINDS.STEP, label: stepName(id), stepId: id });

  const goToStep = (id, { cite: cited = false } = {}) => {
    pickStep(id);
    focusNode(nodes.find((node) => node.id === id));
    if (cited) citeStep(id);
  };

  const selectStep = (id, options) =>
    confirmLeave({ stepId: id }, () => goToStep(id, options));

  const goToState = (stepId, stateId, { cite: cited = true } = {}) => {
    pickStep(stepId);
    setSelectedStateId(stateId || null);
    if (!cited) return;
    if (isNil(stateId)) {
      citeStep(stepId);
      return;
    }
    const state = (
      get(
        nodes.find((node) => node.id === stepId),
        "data.screenStates",
      ) || []
    ).find((item) => item.id === stateId);
    cite({
      kind: CHIP_KINDS.STATE,
      label: `${stepName(stepId)} › ${get(state, "name") || stateId}`,
      stepId,
      stateId,
    });
  };

  const selectState = (stepId, stateId, { force = false, ...options } = {}) => {
    if (force) {
      goToState(stepId, stateId, options);
      return;
    }
    confirmLeave({ stepId, stateId }, () =>
      goToState(stepId, stateId, options),
    );
  };

  const showState = (stepId, stateId) =>
    confirmLeave({ stepId, stateId }, () => {
      goToState(stepId, stateId);
      setPanelTab(STEP_TABS.MAIN);
    });

  const selectFromJourney = (id) =>
    confirmLeave({ stepId: id }, () => {
      pickStep(id);
      citeStep(id);
    });

  const openJourney = () => setJourneyOpen(true);

  useEffect(() => {
    if (!journeyOpen || !flow) return undefined;
    const frame = window.requestAnimationFrame(() =>
      flow.fitView(fitViewOptions),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [journeyOpen, flow]);

  const closeJourney = () => {
    setJourneyOpen(false);
    setChecksOpen(false);
    setSimulation(null);
  };

  const relayout = () => {
    setDirection(LAYOUT_DIRECTIONS.TOP_BOTTOM);
    setNodes((current) =>
      applyLayout(current, edges, LAYOUT_DIRECTIONS.TOP_BOTTOM),
    );
    if (flow)
      window.requestAnimationFrame(() =>
        flow.fitView({ ...fitViewOptions, duration: 300 }),
      );
  };

  const onConnect = useCallback(
    (connection) => {
      if (connection.source === connection.target) return;
      const id = cuid();
      setEdges((current) =>
        addEdge(newRuleEdge({ connection, id, edges: current }), current),
      );
      confirmLeaveRef.current({ stepId: connection.source }, () => {
        setSelectedNodeId(connection.source);
        setSelectedStateId(null);
        setFocusedEdgeId(id);
        setPanelTab(STEP_TABS.NEXT);
      });
    },
    [setEdges, setPanelTab],
  );

  const openEdge = useCallback(
    (edge) =>
      confirmLeaveRef.current({ stepId: edge.source }, () => {
        setSelectedNodeId(edge.source);
        setSelectedStateId(null);
        setFocusedEdgeId(edge.id);
        setPanelTab(STEP_TABS.NEXT);
        setJourneyOpen(false);
      }),
    [setPanelTab],
  );

  const onEdgeClick = useCallback((_, edge) => openEdge(edge), [openEdge]);

  const onNodeClick = (_, node) => selectFromJourney(node.id);

  const updateSelectedNode = (field, value) =>
    setNodes((current) =>
      current.map((node) =>
        node.id === selectedId
          ? { ...node, data: { ...node.data, [field]: value } }
          : node,
      ),
    );

  const insertStep = (id, nodeType) => {
    const anchor = nodes.find((node) => node.id === selectedId) || null;
    const maxOrder = nodes.reduce(
      (max, node) => Math.max(max, parseInt(node.data.order, 10) || 0),
      0,
    );
    const [node] = withHandles(
      [
        {
          ...newStatusNode({
            id,
            name: get(FORM, ["NODE_TYPES", nodeType]) || "",
            nodeType,
            order: String(maxOrder + 1),
            position: newStepPosition(nodes, anchor, direction),
          }),
          selected: true,
        },
      ],
      direction,
    );
    setNodes((current) => [
      ...current.map((item) => ({ ...item, selected: false })),
      node,
    ]);
    setFocusedEdgeId(null);
    setSelectedNodeId(id);
    setSelectedStateId(null);
    setPanelTab(STEP_TABS.MAIN);
    focusNode(node);
  };

  const addStep = (nodeType) => {
    setAddOpen(false);
    const id = cuid();
    confirmLeave({ stepId: id }, () => insertStep(id, nodeType));
  };

  const startAtSelectedStep = () =>
    setNodes((current) => startFrom(current, edges, selectedId));

  const deleteSelectedNode = () => {
    const removed = selectedId;
    setNodes((current) => current.filter((node) => node.id !== removed));
    setEdges((current) =>
      current.filter(
        (edge) => edge.source !== removed && edge.target !== removed,
      ),
    );
    setChips((current) => current.filter((chip) => chip.stepId !== removed));
    setSelectedNodeId(null);
    setSelectedStateId(null);
  };

  const applyImportPlan = (plan) => {
    const graph = applyPlanToGraph({ plan, nodes, edges, direction });
    setNodes(graph.nodes);
    setEdges(graph.edges);
    return graph.steps;
  };

  const stepName = (id) =>
    get(
      nodes.find((node) => node.id === id),
      "data.name",
    ) || id;

  const showBlocked = ({ blockedSteps = [], incompleteScreens = [] }) =>
    Modal.warning({
      title: DRAFT.BLOCKED_TITLE,
      okText: DRAFT.BLOCKED_OK,
      content: (
        <div className="canvas-publish-blocked">
          <p>{DRAFT.BLOCKED_INTRO}</p>
          <ul>
            {blockedSteps.map((step) => (
              <li key={`step-${step.id}`}>
                {t("CANVAS_EDITOR.DRAFT.BLOCKED_STEP", {
                  count: step.clients,
                  name: step.name || stepName(step.id),
                })}
              </li>
            ))}
            {incompleteScreens.map((screen) => (
              <li key={`screen-${screen.versionId}`}>
                {t("CANVAS_EDITOR.DRAFT.INCOMPLETE_SCREEN", {
                  name: screen.stepName || stepName(screen.stepId),
                })}
              </li>
            ))}
          </ul>
        </div>
      ),
    });

  const publishDraft = async () => {
    const outcome = await autosave.flush();
    if (outcome === SAVE_OUTCOMES.CONFLICT) {
      warnConflict();
      if (!(await refreshFromServer())) autosave.failRefresh();
      return;
    }
    if (outcome === SAVE_OUTCOMES.FAILED) {
      notification.error({ message: DRAFT.SAVE_ERROR });
      return;
    }
    const revision = autosave.readRevision();
    const result = await publishCanvas({
      variables: {
        where: { process: { id: process.id } },
        data: { revision: revision > 0 ? revision : null },
      },
    });
    const published = get(result, "data.publishCanvas");
    if (isNil(published)) {
      const code = draftErrorOf(get(result, "errors"));
      if (code === DRAFT_ERRORS.DRAFT_CONFLICT) {
        warnConflict();
        await refreshFromServer();
        return;
      }
      notification.error({
        message: get(DRAFT.ERRORS, code) || DRAFT.PUBLISH_ERROR,
      });
      return;
    }
    if (!published.published) {
      showBlocked(published);
      return;
    }
    notification.success({ message: DRAFT.PUBLISHED });
    await refreshFromServer();
  };

  const applyDraftProposal = async ({ id }) => {
    const outcome = await autosave.flush();
    if (outcome === SAVE_OUTCOMES.CONFLICT) {
      warnConflict();
      if (!(await refreshFromServer())) autosave.failRefresh();
      return false;
    }
    if (outcome === SAVE_OUTCOMES.FAILED) {
      notification.error({ message: DRAFT.SAVE_ERROR });
      return false;
    }
    const result = await applyCanvasProposal({
      variables: { where: { id } },
    });
    const applied = get(result, "data.applyCanvasProposal");
    const errors = get(applied, "errors") || [];
    const details = errors.length ? (
      <ul className="canvas-apply-errors">
        {errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    ) : undefined;
    if (isNil(applied) || !applied.applied) {
      if (draftErrorOf(get(result, "errors")) === DRAFT_ERRORS.DRAFT_CONFLICT) {
        warnConflict();
        await refreshFromServer();
        return false;
      }
      notification.error({
        message: PROPOSAL.APPLY_ERROR,
        description: details,
      });
      return false;
    }
    await refreshFromServer();
    if (errors.length) {
      notification.warning({
        message: PROPOSAL.APPLY_PARTIAL,
        description: details,
      });
      return true;
    }
    notification.success({ message: PROPOSAL.APPLIED_NOTICE });
    return true;
  };

  const applyProposal = (message) => {
    setApplying(true);
    return autosave
      .hold(() => applyDraftProposal(message))
      .finally(() => setApplying(false));
  };

  const publish = async () => {
    if (busy) return;
    setPublishing(true);
    try {
      await autosave.hold(publishDraft);
    } finally {
      setPublishing(false);
    }
  };

  const discardDraft = () =>
    autosave.hold(async () => {
      const result = await discardProcessDraft({
        variables: { where: { process: { id: process.id } } },
      });
      if (!get(result, "data.discardProcessDraft")) {
        notification.error({ message: DRAFT.DISCARD_ERROR });
        return;
      }
      await refreshFromServer();
      notification.success({ message: DRAFT.DISCARDED });
    });

  const confirmDiscard = () =>
    Modal.confirm({
      title: DRAFT.DISCARD_TITLE,
      content: DRAFT.DISCARD_CONTENT,
      okText: DRAFT.DISCARD_OK,
      cancelText: DRAFT.DISCARD_CANCEL,
      okButtonProps: { danger: true },
      onOk: discardDraft,
    });

  const downloadConfig = (config) => {
    const text = serializeConfig(config, "yaml");
    const slug = (process.name || "canvas")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    const blob = new Blob([text], { type: "text/yaml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${slug || "canvas"}-complet.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = () =>
    downloadConfig(
      processToConfig({
        ...process,
        processStatuses: attachGraphScreens(editorStatuses(), nodes),
      }),
    );

  const draftState = () => {
    if (autosave.failing)
      return { tone: DRAFT_TONES.ERROR, text: DRAFT.FAILED, retry: true };
    if (autosave.pending || autosave.saving)
      return { tone: DRAFT_TONES.SAVING, text: DRAFT.SAVING };
    if (!hasDraft) return { tone: DRAFT_TONES.ONLINE, text: DRAFT.ONLINE };
    return {
      tone: DRAFT_TONES.DRAFT,
      text: t("CANVAS_EDITOR.DRAFT.SAVED", { count: changeCount }),
    };
  };
  const status = draftState();

  const shownNodes = useMemo(() => withSharedScreenNames(nodes), [nodes]);
  const orderedSteps = useMemo(() => orderSteps(shownNodes), [shownNodes]);
  const startId = get(orderedSteps, "0.id");
  const selectedNode =
    shownNodes.find((node) => node.id === selectedNodeId) ||
    get(orderedSteps, "0") ||
    null;
  const selectedId = get(selectedNode, "id") || null;
  const needsPaths = useMemo(
    () => (isEmpty(variables) ? null : needsPathsOf(variables)),
    [variables],
  );
  const checkedFields = useMemo(() => checkedFieldsOf(variables), [variables]);
  const checksById = useMemo(
    () =>
      checksByStep(shownNodes, edges, startId, {
        needsPaths,
        checkedFields,
        variables: isEmpty(variables) ? null : variables,
      }),
    [shownNodes, edges, startId, needsPaths, checkedFields, variables],
  );
  const needsFields = useMemo(() => needsFieldsOf(variables), [variables]);
  const recommendationRows = useMemo(
    () => recommendationChecklist({ nodes: orderedSteps, needsFields }),
    [orderedSteps, needsFields],
  );
  const clearRecommendationFocus = useCallback(
    () => setRecommendationFocus(null),
    [],
  );
  const recommendation = useMemo(
    () => ({
      needsFields,
      usage: needsUsage(orderedSteps),
      stepNames: Object.fromEntries(
        orderedSteps.map((node) => [node.id, get(node, "data.name") || ""]),
      ),
      focus: recommendationFocus,
      clearFocus: clearRecommendationFocus,
    }),
    [needsFields, orderedSteps, recommendationFocus, clearRecommendationFocus],
  );
  const simulationResult = useMemo(
    () =>
      simulation
        ? simulateJourney({ nodes: shownNodes, edges, startId, ...simulation })
        : null,
    [simulation, shownNodes, edges, startId],
  );
  const conditionVariables = useMemo(
    () =>
      withRequesterVariables(
        variables,
        t("FORM.POSSIBLE_STATUSES.CONDITIONS", { returnObjects: true }),
      ),
    [variables, t],
  );
  const fieldsToSimulate = useMemo(
    () => simulationFields(edges, conditionVariables),
    [edges, conditionVariables],
  );
  const toggleSimulation = () => {
    setChecksOpen(false);
    if (simulation) {
      setSimulation(null);
      return;
    }
    setSimulation({ values: {}, choices: [] });
    setJourneyOpen(true);
  };
  const toggleChecks = () => {
    setSimulation(null);
    if (checksOpen) {
      setChecksOpen(false);
      return;
    }
    setChecksOpen(true);
    setJourneyOpen(true);
  };
  const allChecks = Object.values(checksById).flat();
  const checksLevel = worstLevel(allChecks);
  const checksTone = checksLevel ? checksLevel.toLowerCase() : "ok";

  const moreItems = [
    {
      key: MORE_ACTIONS.VERIFY,
      label: (
        <span className="canvas-header__menu-label">
          {EDITOR.VERIFY.BUTTON}
          {checksLevel ? (
            <span
              className={`canvas-header__count canvas-header__count--${checksLevel.toLowerCase()}`}
            >
              {allChecks.length}
            </span>
          ) : null}
        </span>
      ),
      icon: (
        <CanvasIcon
          path={checksLevel ? ICON_PATHS.warn : ICON_PATHS.check}
          size={18}
          className={`canvas-header__verify-icon canvas-header__verify-icon--${checksTone}`}
        />
      ),
    },
    {
      key: MORE_ACTIONS.SIMULATE,
      label: EDITOR.SIMULATION.BUTTON,
      icon: <CanvasIcon path={ICON_PATHS.play} size={18} strokeWidth={2} />,
    },
    { type: "divider" },
    {
      key: MORE_ACTIONS.GUEST_LINK,
      label: EDITOR.GUEST_LINK.MENU,
      icon: <MdLink size={18} />,
    },
    {
      key: MORE_ACTIONS.IMPORT,
      label: EDITOR.IMPORT,
      icon: <MdUploadFile size={18} />,
      disabled: busy,
    },
    {
      key: MORE_ACTIONS.IMPORT_FOLDER,
      label: EDITOR.FOLDER_IMPORT.MENU,
      icon: <MdDriveFolderUpload size={18} />,
      disabled: busy,
    },
    {
      key: MORE_ACTIONS.EXPORT,
      label: EDITOR.EXPORT,
      icon: <MdDownload size={18} />,
    },
    {
      key: MORE_ACTIONS.DISCARD,
      label: DRAFT.DISCARD,
      icon: <MdDeleteOutline size={18} />,
      danger: true,
      disabled: busy || (!hasDraft && !unsaved),
    },
  ];

  const onMoreAction = ({ key }) => {
    if (key === MORE_ACTIONS.VERIFY) return toggleChecks();
    if (key === MORE_ACTIONS.SIMULATE) return toggleSimulation();
    if (key === MORE_ACTIONS.GUEST_LINK) return setGuestOpen(true);
    if (key === MORE_ACTIONS.IMPORT) return openImport();
    if (key === MORE_ACTIONS.IMPORT_FOLDER) return openFolderImport();
    if (key === MORE_ACTIONS.DISCARD) return confirmDiscard();
    return handleExport();
  };
  const openCheck = (id, code) =>
    confirmLeave({ stepId: id }, () => {
      setChecksOpen(false);
      setJourneyOpen(false);
      goToStep(id);
      setPanelTab(checkTab(code));
    });
  const openRecommendation = ({ stepId, slotId }) =>
    confirmLeave({ stepId }, () => {
      setChecksOpen(false);
      setJourneyOpen(false);
      goToStep(stepId);
      setPanelTab(STEP_TABS.FIELDS);
      setRecommendationFocus(isNil(slotId) ? null : { stepId, slotId });
    });
  const endIds = useMemo(
    () => new Set(endSteps(shownNodes, edges).map((node) => node.id)),
    [shownNodes, edges],
  );
  const displayedNodes = useMemo(() => {
    const visited = new Set(get(simulationResult, "steps") || []);
    const currentId = get(simulationResult, "stop.stepId");
    const simulationStateOf = (id) => {
      if (!simulationResult) return null;
      if (id === currentId) return "current";
      return visited.has(id) ? "visited" : "dimmed";
    };
    return shownNodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        checks: checksById[node.id],
        tone: stepTone(node, shownNodes, checksById[node.id]),
        isStart: node.id === startId,
        isEnd: endIds.has(node.id),
        simulation: simulationStateOf(node.id),
      },
    }));
  }, [shownNodes, checksById, startId, simulationResult, endIds]);
  const backEdgeCount = edges.filter(isBackEdge).length;
  const edgeLabels = useMemo(
    () => ({
      ...EDITOR.NEXT,
      SHORT_UNITS: EDITOR.STEPS.SHORT_UNITS,
      after: (delay) => t("CANVAS_EDITOR.EDGES.AFTER", { delay }),
    }),
    [EDITOR, t],
  );
  const displayedEdges = useMemo(() => {
    const taken = new Set(get(simulationResult, "edges") || []);
    return decorateEdges(edges, conditionVariables, edgeLabels).map((edge) => {
      const withOpen = {
        ...edge,
        data: { ...edge.data, onOpen: () => openEdge(edge) },
      };
      if (taken.has(edge.id))
        return {
          ...withOpen,
          animated: true,
          className: classNames(withOpen.className, "canvas-edge--simulated"),
        };
      const shown =
        !showBack && isBackEdge(edge)
          ? { ...withOpen, hidden: true }
          : withOpen;
      return simulationResult
        ? {
            ...shown,
            className: classNames(shown.className, "canvas-edge--dimmed"),
          }
        : shown;
    });
  }, [
    edges,
    conditionVariables,
    edgeLabels,
    showBack,
    simulationResult,
    openEdge,
  ]);
  const selectedNodePersisted =
    selectedNode !== null && liveStepIds.includes(selectedNode.id);
  const liveSelected = liveSnapshot.find((status) => status.id === selectedId);

  const selectStepRef = useRef(selectStep);
  selectStepRef.current = selectStep;
  const selectStateRef = useRef(selectState);
  selectStateRef.current = selectState;
  const selection = useMemo(
    () => ({
      stepId: selectedId,
      stateId: selectedStateId,
      chips,
      selectStep: (id, options) => selectStepRef.current(id, options),
      selectState: (stepId, stateId, options) =>
        selectStateRef.current(stepId, stateId, options),
      moveStates,
      guardLeave,
      cite,
      citeOnly,
      uncite,
    }),
    [
      selectedId,
      selectedStateId,
      chips,
      moveStates,
      guardLeave,
      cite,
      citeOnly,
      uncite,
    ],
  );

  return (
    <CanvasSelectionContext.Provider value={selection}>
      <RecommendationContext.Provider value={recommendation}>
        <div className="canvas-editor">
          <input
            ref={fileInputRef}
            type="file"
            accept=".yaml,.yml,.json"
            className="canvas-editor__hidden"
            onChange={onImport}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            className="canvas-editor__hidden"
            onChange={onPickFolder}
            {...FOLDER_PICKER}
          />
          {folderFiles ? (
            <FolderImportModal
              files={folderFiles}
              nodes={nodes}
              processId={process.id}
              liveStepIds={liveStepIds}
              onApplyPlan={applyImportPlan}
              onClose={closeFolderImport}
            />
          ) : null}
          <ImportSummaryModal
            file={pendingFile}
            existingProcess={importBase}
            liveStepIds={liveStepIds}
            onImportSteps={putImportedSteps}
            requireEmbeddedScreens
            onClose={closeImport}
          />
          <header className="canvas-header">
            <div className="canvas-header__title">
              <button
                type="button"
                className="canvas-header__icon-button"
                aria-label={EDITOR.BACK_TO_LIST}
                title={EDITOR.BACK_TO_LIST}
                onClick={leave}
              >
                <CanvasIcon
                  path={ICON_PATHS.arrowLeft}
                  size={20}
                  strokeWidth={2}
                />
              </button>
              <button
                type="button"
                className="canvas-header__back"
                title={EDITOR.BACK_TO_LIST}
                onClick={leave}
              >
                {CANVASES.TITLE}
              </button>
              <CanvasIcon
                path={ICON_PATHS.chevronRight}
                strokeWidth={2}
                className="canvas-header__separator"
              />
              <span className="canvas-header__name">{process.name}</span>
              {THEMES[process.theme] ? (
                <span className="canvas-header__chip">
                  {THEMES[process.theme]}
                </span>
              ) : null}
            </div>
            {status.retry ? (
              <button
                type="button"
                className={`canvas-header__draft canvas-header__draft--${status.tone}`}
                onClick={autosave.retry}
              >
                {status.text}
              </button>
            ) : (
              <span
                className={`canvas-header__draft canvas-header__draft--${status.tone}`}
              >
                {status.text}
              </span>
            )}
            <button
              type="button"
              className="canvas-header__button canvas-header__button--primary"
              onClick={publish}
              disabled={!publishable}
            >
              <CanvasIcon path={WORKSHOP_ICONS.upload} strokeWidth={2.2} />
              {DRAFT.PUBLISH}
            </button>
            <Dropdown
              trigger={["click"]}
              menu={{ items: moreItems, onClick: onMoreAction }}
            >
              <button
                type="button"
                className="canvas-header__icon-button"
                aria-label={EDITOR.MORE_ACTIONS}
              >
                <CanvasIcon path={ICON_PATHS.dots} size={20} strokeWidth={3} />
              </button>
            </Dropdown>
          </header>
          <div
            className={classNames("canvas-editor__layout", {
              "canvas-editor__layout--busy": busy,
            })}
            inert={busy ? "" : undefined}
            aria-busy={busy}
          >
            <div className="canvas-editor__main">
              <StepList
                nodes={shownNodes}
                edges={edges}
                checksById={checksById}
                selectedId={selectedId}
                selectedStateId={selectedStateId}
                variables={variables}
                onSelect={(id) => selectStep(id, { cite: true })}
                onSelectState={showState}
                onOpenJourney={openJourney}
                onAdd={() => setAddOpen(true)}
              />
              <div className="canvas-editor__workspace">
                {selectedNode ? (
                  <StatusPanel
                    key={selectedNode.id}
                    node={selectedNode}
                    nodes={shownNodes}
                    edges={edges}
                    focusedEdgeId={focusedEdgeId}
                    activeTab={panelTab}
                    onTabChange={setPanelTab}
                    onEdgesChange={setEdges}
                    processId={process.id}
                    persisted={selectedNodePersisted}
                    variables={variables}
                    liveFormDefinition={get(liveSelected, "formDefinition")}
                    onChange={updateSelectedNode}
                    isStart={selectedId === startId}
                    onStartHere={startAtSelectedStep}
                    onDelete={deleteSelectedNode}
                    reload={reload}
                    sideBody={sideBody}
                    sideMeta={sideMeta}
                    sideTab={sideTab}
                    onSideTab={setSideTab}
                    sideOpen={sidePanel === SIDE_PANELS.SETTINGS}
                    onSideOpen={(open) =>
                      setSidePanel(open ? SIDE_PANELS.SETTINGS : null)
                    }
                  />
                ) : (
                  <div className="canvas-editor__empty">
                    {EDITOR.WORKSPACE.EMPTY}
                  </div>
                )}
              </div>
              <JourneyOverlay
                open={journeyOpen}
                title={JOURNEY.TITLE}
                summary={[
                  t("CANVAS_EDITOR.JOURNEY.STEP_COUNT", {
                    count: nodes.length,
                  }),
                  t("CANVAS_EDITOR.JOURNEY.RULE_COUNT", {
                    count: edges.length,
                  }),
                  JOURNEY.HINT,
                ].join(" · ")}
                openLabel={
                  selectedNode
                    ? t("CANVAS_EDITOR.JOURNEY.OPEN", {
                        name: selectedNode.data.name || EDITOR.STEPS.UNNAMED,
                      })
                    : ""
                }
                closeLabel={JOURNEY.CLOSE}
                onOpenSelected={closeJourney}
                onClose={closeJourney}
              >
                <div className="canvas-editor__viewport">
                  <ReactFlow
                    nodes={displayedNodes}
                    edges={displayedEdges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onEdgeClick={onEdgeClick}
                    onNodeClick={onNodeClick}
                    onPaneClick={() => setFocusedEdgeId(null)}
                    onInit={(instance) => {
                      setFlow(instance);
                      setZoom(instance.getZoom());
                    }}
                    onMove={(_, viewport) => setZoom(viewport.zoom)}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    fitView
                    fitViewOptions={fitViewOptions}
                    minZoom={0.2}
                    deleteKeyCode={null}
                    proOptions={{ hideAttribution: true }}
                  >
                    <Panel position="top-left" className="canvas-toolbar">
                      {backEdgeCount ? (
                        <button
                          type="button"
                          className="canvas-tool"
                          aria-pressed={showBack}
                          onClick={() => setShowBack((shown) => !shown)}
                        >
                          <span
                            className={classNames("canvas-tool__switch", {
                              "canvas-tool__switch--on": showBack,
                            })}
                          />
                          {t("CANVAS_EDITOR.SHOW_BACK", {
                            count: backEdgeCount,
                          })}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="canvas-tool"
                        onClick={relayout}
                      >
                        <CanvasIcon path={ICON_PATHS.layout} size={16} />
                        {EDITOR.RELAYOUT}
                      </button>
                    </Panel>
                    <Panel position="top-right" className="canvas-toolbar">
                      <div className="canvas-zoom">
                        <button
                          type="button"
                          aria-label={EDITOR.TOOLBAR.ZOOM_OUT}
                          onClick={() =>
                            flow && flow.zoomOut({ duration: ZOOM_DURATION })
                          }
                        >
                          −
                        </button>
                        <span>{`${Math.round(zoom * 100)} %`}</span>
                        <button
                          type="button"
                          aria-label={EDITOR.TOOLBAR.ZOOM_IN}
                          onClick={() =>
                            flow && flow.zoomIn({ duration: ZOOM_DURATION })
                          }
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        className="canvas-tool"
                        onClick={() =>
                          flow &&
                          flow.fitView({
                            ...fitViewOptions,
                            duration: ZOOM_DURATION,
                          })
                        }
                      >
                        {EDITOR.TOOLBAR.FIT}
                      </button>
                    </Panel>
                    <Panel position="bottom-left" className="canvas-legend">
                      {LEGEND_TONES.map((tone) => (
                        <span key={tone} className="canvas-legend__item">
                          <span
                            className={`canvas-node__dot canvas-node__dot--${tone}`}
                          />
                          {EDITOR.LEGEND[tone.toUpperCase()]}
                        </span>
                      ))}
                    </Panel>
                    <Panel position="bottom-right" className="canvas-hint">
                      {EDITOR.LEGEND.CONNECT[direction]}
                    </Panel>
                    <Background color="#cdd6e2" gap={20} size={2} />
                  </ReactFlow>
                </div>
                {simulationResult ? (
                  <SimulationPanel
                    steps={orderedSteps}
                    fields={fieldsToSimulate}
                    values={simulation.values}
                    result={simulationResult}
                    canGoBack={simulation.choices.length > 0}
                    onValueChange={(accessor, value) =>
                      setSimulation((current) => ({
                        ...current,
                        values: { ...current.values, [accessor]: value },
                      }))
                    }
                    onChoose={(choice) =>
                      setSimulation((current) => ({
                        ...current,
                        choices: [...current.choices, choice],
                      }))
                    }
                    onBack={() =>
                      setSimulation((current) => ({
                        ...current,
                        choices: current.choices.slice(0, -1),
                      }))
                    }
                    onRestart={() =>
                      setSimulation((current) => ({ ...current, choices: [] }))
                    }
                    onSelectStep={selectStep}
                    onClose={() => setSimulation(null)}
                  />
                ) : null}
                {checksOpen ? (
                  <ChecksPanel
                    steps={orderedSteps}
                    checksById={checksById}
                    ends={endSteps(orderedSteps, edges)}
                    recommendation={recommendationRows}
                    onOpen={openCheck}
                    onOpenRecommendation={openRecommendation}
                    onClose={() => setChecksOpen(false)}
                  />
                ) : null}
              </JourneyOverlay>
            </div>
            <ClaudePanel
              processId={process.id}
              nodes={shownNodes}
              edges={edges}
              onApply={applyProposal}
              open={sidePanel === SIDE_PANELS.LUCIE}
              onClose={() => setSidePanel(null)}
            />
            <StepSidePanel
              open={sidePanel === SIDE_PANELS.SETTINGS}
              title={EDITOR.SIDE.SETTINGS}
              subtitle={get(selectedNode, "data.name") || null}
              closeLabel={EDITOR.SIDE.CLOSE_SETTINGS}
              onClose={() => setSidePanel(null)}
              onBody={setSideBody}
              onMeta={setSideMeta}
            />
            <SideRail
              open={sidePanel}
              labels={EDITOR.SIDE}
              onToggle={(panel) =>
                setSidePanel((current) => (current === panel ? null : panel))
              }
            />
          </div>
          <AddStepDialog
            open={addOpen}
            onPick={addStep}
            onClose={() => setAddOpen(false)}
          />
          <GuestLinkDialog
            open={guestOpen}
            processId={process.id}
            onClose={closeGuest}
          />
        </div>
      </RecommendationContext.Provider>
    </CanvasSelectionContext.Provider>
  );
};

export default View;
